import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";
import Sidebar from "@/components/Sidebar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Separator } from "@/components/ui/separator";
import { Star, Plus, MagnifyingGlass, Crown, TrendUp, Users, Gift, CurrencyDollar, PencilSimple, Trash, Clock, Download } from "@phosphor-icons/react";
import { downloadCsv } from "@/utils/downloadCsv";

const tierColors = {
  bronze: "bg-orange-100 text-orange-800 border-orange-200",
  silver: "bg-gray-100 text-gray-700 border-gray-300",
  gold: "bg-yellow-100 text-yellow-800 border-yellow-300",
  platinum: "bg-purple-100 text-purple-800 border-purple-300",
};

const tierBgColors = {
  bronze: "from-orange-50 to-orange-100/50",
  silver: "from-gray-50 to-gray-100/50",
  gold: "from-yellow-50 to-yellow-100/50",
  platinum: "from-purple-50 to-purple-100/50",
};

const LoyaltyPage = ({ embedded = false }) => {
  const [customers, setCustomers] = useState([]);
  const [tierInfo, setTierInfo] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEarnDialog, setShowEarnDialog] = useState(false);
  const [showRedeemDialog, setShowRedeemDialog] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [newCustomer, setNewCustomer] = useState({ name: "", email: "", phone: "" });
  const [earnAmount, setEarnAmount] = useState("");
  const [redeemPoints, setRedeemPoints] = useState("");
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editForm, setEditForm] = useState({ name: "", email: "", phone: "" });
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [showHistoryDialog, setShowHistoryDialog] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyData, setHistoryData] = useState(null);

  const openHistoryDialog = async (customer) => {
    setSelectedCustomer(customer);
    setShowHistoryDialog(true);
    setHistoryLoading(true);
    setHistoryData(null);
    try {
      const r = await axios.get(`${API}/loyalty/perk-history/${customer.id}`);
      setHistoryData(r.data);
    } catch (e) {
      toast.error("Couldn't load perk history");
    } finally {
      setHistoryLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [custRes, tierRes] = await Promise.all([
        axios.get(`${API}/loyalty/customers${search ? `?search=${search}` : ""}`),
        axios.get(`${API}/loyalty/tiers`)
      ]);
      setCustomers(custRes.data);
      setTierInfo(tierRes.data.tiers);
    } catch (error) {
      console.error("Failed to load loyalty data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async () => {
    try {
      const res = await axios.get(`${API}/loyalty/customers?search=${search}`);
      setCustomers(res.data);
    } catch (error) {
      toast.error("Search failed");
    }
  };

  const handleAddCustomer = async () => {
    if (!newCustomer.name || !newCustomer.email || !newCustomer.phone) {
      toast.error("Please fill all fields");
      return;
    }
    try {
      await axios.post(`${API}/loyalty/customers`, newCustomer);
      toast.success("Loyalty member added!");
      setShowAddDialog(false);
      setNewCustomer({ name: "", email: "", phone: "" });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add customer");
    }
  };

  const handleEarnPoints = async () => {
    if (!earnAmount || !selectedCustomer) return;
    try {
      const res = await axios.post(`${API}/loyalty/earn`, {
        customer_id: selectedCustomer.id,
        order_total: parseFloat(earnAmount),
      });
      toast.success(`${res.data.points_earned} points earned! ${res.data.tier_upgraded ? `Tier upgraded to ${res.data.tier.toUpperCase()}!` : ""}`);
      setShowEarnDialog(false);
      setEarnAmount("");
      fetchData();
    } catch (error) {
      toast.error("Failed to earn points");
    }
  };

  const handleRedeemPoints = async () => {
    if (!redeemPoints || !selectedCustomer) return;
    try {
      const res = await axios.post(`${API}/loyalty/redeem`, {
        customer_id: selectedCustomer.id,
        points: parseInt(redeemPoints)
      });
      toast.success(`Redeemed! $${res.data.discount_amount.toFixed(2)} discount applied.`);
      setShowRedeemDialog(false);
      setRedeemPoints("");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to redeem");
    }
  };

  const openEditDialog = (c) => {
    setSelectedCustomer(c);
    setEditForm({ name: c.name || "", email: c.email || "", phone: c.phone || "" });
    setShowEditDialog(true);
  };

  const handleSaveEdit = async () => {
    if (!selectedCustomer) return;
    if (!editForm.name.trim() || !editForm.email.trim()) {
      toast.error("Name and email are required");
      return;
    }
    try {
      await axios.put(`${API}/loyalty/customers/${selectedCustomer.id}`, editForm);
      toast.success("Member updated");
      setShowEditDialog(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update");
    }
  };

  const handleDeleteMember = async () => {
    if (!deleteConfirm) return;
    try {
      await axios.delete(`${API}/loyalty/customers/${deleteConfirm.id}`);
      toast.success("Member deleted");
      setDeleteConfirm(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete");
    }
  };

  const totalMembers = customers.length;
  const goldPlus = customers.filter(c => ["gold", "platinum"].includes(c.tier)).length;

  return (
    <div className="min-h-screen bg-background" data-testid="loyalty-page">
      {!embedded && <Sidebar />}
      <main className={embedded ? "" : "main-content p-6"}>
        <div className="flex items-center justify-between mb-6">
          <div>
            {!embedded && <h1 className="font-heading text-3xl font-medium">Loyalty Program</h1>}
            <p className="text-muted-foreground">Manage customer rewards and tiers</p>
          </div>
            {!embedded && (
              <Button className="gap-2" onClick={() => setShowAddDialog(true)} data-testid="add-loyalty-btn">
                <Plus size={18} /> Add Member
              </Button>
            )}
        </div>

        {/* Summary */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <Card><CardContent className="p-4 flex items-center gap-4">
            <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center"><Users size={24} className="text-primary" weight="fill" /></div>
            <div><p className="text-sm text-muted-foreground">Total Members</p><p className="font-mono text-2xl font-medium">{totalMembers}</p></div>
          </CardContent></Card>
          <Card><CardContent className="p-4 flex items-center gap-4">
            <div className="w-12 h-12 bg-yellow-100 rounded-xl flex items-center justify-center"><Crown size={24} className="text-yellow-600" weight="fill" /></div>
            <div><p className="text-sm text-muted-foreground">Gold+ Members</p><p className="font-mono text-2xl font-medium">{goldPlus}</p></div>
          </CardContent></Card>
          <Card><CardContent className="p-4 flex items-center gap-4">
            <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center"><TrendUp size={24} className="text-green-600" weight="fill" /></div>
            <div><p className="text-sm text-muted-foreground">Total Revenue</p><p className="font-mono text-2xl font-medium">${customers.reduce((s, c) => s + (c.total_spent || 0), 0).toFixed(0)}</p></div>
          </CardContent></Card>
          <Card><CardContent className="p-4 flex items-center gap-4">
            <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center"><Star size={24} className="text-blue-600" weight="fill" /></div>
            <div><p className="text-sm text-muted-foreground">Total Points</p><p className="font-mono text-2xl font-medium">{customers.reduce((s, c) => s + (c.points || 0), 0).toLocaleString()}</p></div>
          </CardContent></Card>
        </div>

        {/* Tier Info */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-6">
          {tierInfo.map(tier => (
            <div key={tier.name} className={`bg-gradient-to-br ${tierBgColors[tier.name]} rounded-xl p-4 border border-border`}>
              <div className="flex items-center gap-2 mb-2">
                <Crown size={18} weight="fill" className={tier.name === "gold" ? "text-yellow-600" : tier.name === "platinum" ? "text-purple-600" : tier.name === "silver" ? "text-gray-500" : "text-orange-500"} />
                <span className="font-heading font-medium capitalize">{tier.name}</span>
                <span className="text-xs text-muted-foreground ml-auto">{tier.multiplier}x pts</span>
              </div>
              <p className="text-xs text-muted-foreground mb-1">${tier.threshold}+ spent</p>
              <div className="space-y-0.5">{tier.perks.map((p, i) => <p key={p.id || i} className="text-xs text-muted-foreground">- {typeof p === "string" ? p : p.label}</p>)}</div>
            </div>
          ))}
        </div>

        {/* Search */}
        <div className="flex items-center gap-3 mb-4">
          <div className="relative flex-1 max-w-md">
            <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
            <Input placeholder="Search by name, email, or phone..." value={search} onChange={(e) => setSearch(e.target.value)} onKeyDown={(e) => e.key === "Enter" && handleSearch()} className="pl-10" data-testid="loyalty-search" />
          </div>
          <Button variant="outline" onClick={handleSearch}>Search</Button>
        </div>

        {/* Customer List */}
        <Card>
          <CardHeader><CardTitle className="font-heading text-lg">Members</CardTitle></CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-12"><div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin"></div></div>
            ) : customers.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground"><Star size={48} className="mx-auto mb-2 opacity-30" /><p>No loyalty members yet</p></div>
            ) : (
              <div className="space-y-2">
                {customers.map(customer => (
                  <div key={customer.id} className="flex items-center justify-between p-4 bg-secondary/30 rounded-xl border border-border hover:border-primary/20 transition-colors" data-testid={`loyalty-customer-${customer.id}`}>
                    <div className="flex items-center gap-4">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${tierColors[customer.tier] || tierColors.bronze}`}>
                        <Crown size={18} weight="fill" />
                      </div>
                      <div>
                        <p className="font-medium">{customer.name}</p>
                        <p className="text-sm text-muted-foreground">{customer.email} | {customer.phone}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <p className="font-mono font-medium">{(customer.points || 0).toLocaleString()} pts</p>
                        <p className="text-xs text-muted-foreground">${(customer.total_spent || 0).toFixed(2)} spent | {customer.visit_count || 0} visits</p>
                      </div>
                      <Badge className={`${tierColors[customer.tier] || tierColors.bronze} border capitalize`}>{customer.tier}</Badge>
                      <div className="flex gap-1">
                        <Button size="sm" variant="outline" className="gap-1" onClick={() => { setSelectedCustomer(customer); setShowEarnDialog(true); }} data-testid={`earn-${customer.id}`}>
                          <Plus size={14} /> Earn
                        </Button>
                        <Button size="sm" variant="outline" className="gap-1" onClick={() => { setSelectedCustomer(customer); setShowRedeemDialog(true); }} data-testid={`redeem-${customer.id}`}>
                          <Gift size={14} /> Redeem
                        </Button>
                        <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => openHistoryDialog(customer)} title="Perk history" data-testid={`history-loyalty-${customer.id}`}>
                          <Clock size={14} />
                        </Button>
                        <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => openEditDialog(customer)} title="Edit" data-testid={`edit-loyalty-${customer.id}`}>
                          <PencilSimple size={14} />
                        </Button>
                        <Button size="icon" variant="ghost" className="h-8 w-8 text-destructive hover:text-destructive hover:bg-destructive/10" onClick={() => setDeleteConfirm(customer)} title="Delete" data-testid={`delete-loyalty-${customer.id}`}>
                          <Trash size={14} />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </main>

      {/* Add Customer Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent data-testid="add-loyalty-dialog">
          <DialogHeader><DialogTitle className="font-heading text-xl">Add Loyalty Member</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2"><Label>Name</Label><Input placeholder="Full name" value={newCustomer.name} onChange={(e) => setNewCustomer(p => ({ ...p, name: e.target.value }))} data-testid="loyalty-name" /></div>
            <div className="space-y-2"><Label>Email</Label><Input type="email" placeholder="email@example.com" value={newCustomer.email} onChange={(e) => setNewCustomer(p => ({ ...p, email: e.target.value }))} data-testid="loyalty-email" /></div>
            <div className="space-y-2"><Label>Phone</Label><Input placeholder="555-1234" value={newCustomer.phone} onChange={(e) => setNewCustomer(p => ({ ...p, phone: e.target.value }))} data-testid="loyalty-phone" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddDialog(false)}>Cancel</Button>
            <Button onClick={handleAddCustomer} data-testid="save-loyalty-btn">Add Member</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Earn Points Dialog */}
      <Dialog open={showEarnDialog} onOpenChange={setShowEarnDialog}>
        <DialogContent data-testid="earn-dialog">
          <DialogHeader><DialogTitle className="font-heading text-xl">Earn Points</DialogTitle></DialogHeader>
          {selectedCustomer && (
            <div className="py-2">
              <p className="text-sm text-muted-foreground mb-4">
                {selectedCustomer.name} ({selectedCustomer.tier.toUpperCase()}) - {TIER_MULTIPLIERS_DISPLAY[selectedCustomer.tier] || "1.0"}x multiplier
              </p>
              <div className="space-y-2">
                <Label>Order Total ($)</Label>
                <Input type="number" placeholder="0.00" value={earnAmount} onChange={(e) => setEarnAmount(e.target.value)} data-testid="earn-amount" />
              </div>
              {earnAmount && (
                <p className="text-sm text-accent mt-2">
                  Points to earn: ~{Math.round(parseFloat(earnAmount) * (TIER_MULTIPLIERS_DISPLAY[selectedCustomer.tier] || 1))}
                </p>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEarnDialog(false)}>Cancel</Button>
            <Button onClick={handleEarnPoints} data-testid="confirm-earn-btn">Add Points</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Redeem Points Dialog */}
      <Dialog open={showRedeemDialog} onOpenChange={setShowRedeemDialog}>
        <DialogContent data-testid="redeem-dialog">
          <DialogHeader><DialogTitle className="font-heading text-xl">Redeem Points</DialogTitle></DialogHeader>
          {selectedCustomer && (
            <div className="py-2">
              <p className="text-sm text-muted-foreground mb-2">
                {selectedCustomer.name} - Available: {(selectedCustomer.points || 0).toLocaleString()} points
              </p>
              <p className="text-xs text-muted-foreground mb-4">100 points = $1.00 discount</p>
              <div className="space-y-2">
                <Label>Points to Redeem</Label>
                <Input type="number" placeholder="100" value={redeemPoints} onChange={(e) => setRedeemPoints(e.target.value)} max={selectedCustomer.points} data-testid="redeem-amount" />
              </div>
              {redeemPoints && (
                <p className="text-sm text-accent mt-2">
                  Discount: ${(parseInt(redeemPoints) / 100).toFixed(2)}
                </p>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRedeemDialog(false)}>Cancel</Button>
            <Button onClick={handleRedeemPoints} data-testid="confirm-redeem-btn">Redeem</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Member Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent data-testid="edit-loyalty-dialog">
          <DialogHeader><DialogTitle className="font-heading text-xl">Edit Loyalty Member</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div className="space-y-2">
              <Label>Name</Label>
              <Input value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} data-testid="edit-loyalty-name" />
            </div>
            <div className="space-y-2">
              <Label>Email</Label>
              <Input type="email" value={editForm.email} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} data-testid="edit-loyalty-email" />
            </div>
            <div className="space-y-2">
              <Label>Phone</Label>
              <Input value={editForm.phone} onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })} data-testid="edit-loyalty-phone" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>Cancel</Button>
            <Button onClick={handleSaveEdit} data-testid="save-edit-loyalty-btn">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Perk History Dialog */}
      <Dialog open={showHistoryDialog} onOpenChange={setShowHistoryDialog}>
        <DialogContent className="sm:max-w-[640px]" data-testid="perk-history-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl flex items-center gap-2">
              <Clock size={20} className="text-accent" />
              Perk History
              {selectedCustomer && <span className="text-muted-foreground font-sans font-normal text-base">· {selectedCustomer.name}</span>}
            </DialogTitle>
          </DialogHeader>
          <div className="py-2 space-y-3">
            {historyLoading && (
              <div className="py-8 text-center text-sm text-muted-foreground">Loading…</div>
            )}
            {!historyLoading && historyData && (
              <>
                <div className="grid grid-cols-2 gap-3">
                  <div className="rounded-lg border border-border p-3" data-testid="perk-history-count">
                    <div className="text-xs uppercase text-muted-foreground">Redemptions</div>
                    <div className="text-2xl font-mono font-semibold">{historyData.count}</div>
                  </div>
                  <div className="rounded-lg border border-border p-3" data-testid="perk-history-total-saved">
                    <div className="text-xs uppercase text-muted-foreground">Total Saved</div>
                    <div className="text-2xl font-mono font-semibold text-accent">${historyData.total_saved.toFixed(2)}</div>
                  </div>
                </div>

                {historyData.redemptions.length === 0 ? (
                  <div className="py-6 text-center text-sm text-muted-foreground rounded-lg bg-secondary/30" data-testid="perk-history-empty">
                    No perk redemptions yet.
                  </div>
                ) : (
                  <div className="rounded-lg border border-border divide-y divide-border max-h-80 overflow-y-auto">
                    {historyData.redemptions.map((r) => (
                      <div key={r.id} className="p-3 flex items-center justify-between gap-3 text-sm" data-testid={`perk-history-row-${r.id}`}>
                        <div className="min-w-0">
                          <div className="font-medium truncate">{r.perk_label || "(perk removed)"}</div>
                          <div className="text-xs text-muted-foreground">
                            {new Date(r.created_at).toLocaleString()} · {r.tier_name?.toUpperCase()} · by {r.redeemed_by || "—"}
                          </div>
                        </div>
                        <div className="font-mono font-semibold text-accent shrink-0">−${(r.discount_amount || 0).toFixed(2)}</div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowHistoryDialog(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={!!deleteConfirm} onOpenChange={(o) => !o && setDeleteConfirm(null)}>
        <DialogContent data-testid="delete-loyalty-dialog">
          <DialogHeader><DialogTitle className="font-heading text-xl">Delete Loyalty Member</DialogTitle></DialogHeader>
          <div className="py-2">
            <p className="text-sm text-muted-foreground">
              Are you sure you want to delete <strong className="text-foreground">{deleteConfirm?.name}</strong>?
              This will permanently remove their {deleteConfirm?.points || 0} points and visit history. This cannot be undone.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteConfirm(null)}>Cancel</Button>
            <Button variant="destructive" onClick={handleDeleteMember} data-testid="confirm-delete-loyalty-btn">Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const TIER_MULTIPLIERS_DISPLAY = { bronze: 1.0, silver: 1.25, gold: 1.5, platinum: 2.0 };

export default LoyaltyPage;
